/* =============================================================================
   ONENESS — "UNTIL NEXT TIME" portal easter egg
   -----------------------------------------------------------------------------
   Attaches to the existing footer wordmark. Makes only the word TIME clickable.

   NON-DESTRUCTIVE BY DESIGN:
   - No markup changes. The word is located with a DOM Range, not by wrapping
     it in a span (wrapping would break the footer's background-clip:text
     gradient, which paints per element).
   - No stylesheet changes. All CSS is injected under .unt-* class names.
   - The only property ever written to a theme element is a temporary
     mask-image on the wordmark span, which hides the word while its particles
     are in flight. It is cleared when the sequence ends.
   - Overlays are appended to <body>, never inside the footer, because
     .fo-wordmark is overflow:hidden, its span has a transform (a containing
     block for fixed children) and .footer-oneness is isolation:isolate.

   INSTALL — pick one:
   A) Paste this whole file at the end of assets/custom.js  (already loaded
      site-wide with defer; nothing else to change).
   B) Upload as assets/until-next-time.js and add a deferred script tag to
      layout/theme.liquid pointing at:  {{ 'until-next-time.js' | asset_url }}

   Everything tunable lives in CFG below.
   ========================================================================== */
(function () {
  'use strict';

  if (window.__untInit) return;
  window.__untInit = true;

  var CFG = {
    /* what to hook ------------------------------------------------------- */
    wordmarkSelector : '.footer-oneness .fo-wordmark span',
    word             : 'TIME',          // the clickable word (last match wins)

    /* the message -------------------------------------------------------- */
    lines : [
      'Time is life’s greatest luxury.',
      'Thank you for sharing a small piece of yours with us.'
    ],
    messageWeight     : 800,            // matches the wordmark's font-weight
    messageUppercase  : true,
    messageTracking   : '.26em',
    hintText          : 'press esc, or click anywhere',

    /* timing (ms) -------------------------------------------------------- */
    blackout    : 850,     // site fades to black — finishes before the burst
    shimmer     : 1000,    // light sweep across the letters (now canvas-owned)
    fracture    : 640,     // outward burst before the spiral takes over
    gather      : 1750,    // inward spiral -> ring
    msgIn       : 2000,    // after the portal forms
    lineGap     : 720,     // between lines
    msgHold     : 3400,    // after the last line has landed
    quietHint   : 900,     // before the exit hint appears
    autoReturn  : 4500,    // before it closes itself

    /* look --------------------------------------------------------------- */
    portalYRatio   : 0.455,
    sound          : true,
    zIndex         : 99990,  // under this theme's popups (999999) & cart (100000)
    hitZIndex      : 9999
  };

  /* ------------------------------------------------------------------ dom */
  var span, textNode, wordStart, doc = document;
  var canvas, ctx, msg, hint, hitArea, srLive;
  var lineEls = [];

  var mqReduce = window.matchMedia('(prefers-reduced-motion: reduce)');

  /* ---------------------------------------------------------------- state */
  var state = 'idle';                       // idle|shimmer|fracture|portal|return
  var closing = false;
  var phaseT = 0, portalT = 0, returnT = 0, shimmerT = 0, gatherG = 0;
  var particles = [], dust = [], sprites = [];
  var glyph = null, sweep = null, wordCanvas = null, wordDraw = null, masked = false;
  var raf = null, lastTs = 0, acc = 0, STEP = 1000 / 60;
  var W = 0, H = 0, DPR = 1, cx = 0, cy = 0, R = 160;
  var portalAlpha = 0, veilA = 0, reduced = false;
  var msgOut = false, openedAt = 0, lockY = 0, hitPending = false;
  var wordBox = null;

  /* =========================================================== bootstrap */
  function init() {
    span = doc.querySelector(CFG.wordmarkSelector);
    if (!span) return;

    var found = findWord(span, CFG.word);
    if (!found) return;
    textNode = found.node; wordStart = found.index;

    injectStyles();
    buildLayers();
    buildSprites();
    placeHitArea();

    if (window.ResizeObserver) {
      var ro = new ResizeObserver(function () { if (state === 'idle') placeHitArea(); });
      ro.observe(span);
    }
    window.addEventListener('resize', function () {
      if (state === 'idle') { sizeCanvas(); placeOverlays(); placeHitArea(); }
      else forceClose();
    });
    if (doc.fonts && doc.fonts.ready) doc.fonts.ready.then(function () {
      if (state === 'idle') placeHitArea();
    });
    // the page keeps reflowing as lazy images land — keep the hit target honest
    window.addEventListener('load', function () { if (state === 'idle') placeHitArea(); });
    window.addEventListener('scroll', function () {
      if (state !== 'idle' || hitPending) return;
      hitPending = true;
      requestAnimationFrame(function () { hitPending = false; placeHitArea(); });
    }, { passive: true });
    // rAF is frozen in a hidden tab; don't leave the sequence stranded mid-flight
    doc.addEventListener('visibilitychange', function () {
      if (doc.hidden && state !== 'idle') { closing = true; finish(); }
    });

    sizeCanvas();
    placeOverlays();
  }

  function findWord(root, word) {
    var target = word.toUpperCase(), hit = null;
    (function walk(n) {
      for (var i = 0; i < n.childNodes.length; i++) {
        var c = n.childNodes[i];
        if (c.nodeType === 3) {
          var k = c.nodeValue.toUpperCase().lastIndexOf(target);
          if (k > -1) hit = { node: c, index: k };
        } else if (c.nodeType === 1) walk(c);
      }
    })(root);
    return hit;
  }

  /* ------------------------------------------------------- geometry utils */
  function rangeFor(from, to) {
    var r = doc.createRange();
    r.setStart(textNode, from);
    r.setEnd(textNode, to);
    return r;
  }
  function wordRect() {
    return rangeFor(wordStart, wordStart + CFG.word.length).getBoundingClientRect();
  }
  function charRects() {
    var out = [];
    for (var i = 0; i < CFG.word.length; i++) {
      out.push({
        ch: textNode.nodeValue.substr(wordStart + i, 1),
        rect: rangeFor(wordStart + i, wordStart + i + 1).getBoundingClientRect()
      });
    }
    return out;
  }
  function baselineOffset() {
    var cs = getComputedStyle(span);
    var probe = doc.createElement('div');
    probe.style.cssText = 'position:absolute;left:-9999px;top:0;visibility:hidden;white-space:nowrap';
    probe.style.font = cs.fontStyle + ' ' + cs.fontWeight + ' ' + cs.fontSize + '/' + cs.lineHeight + ' ' + cs.fontFamily;
    probe.style.letterSpacing = cs.letterSpacing;
    var g = doc.createElement('span'); g.textContent = CFG.word;
    var m = doc.createElement('span');
    m.style.cssText = 'display:inline-block;width:0;height:0;overflow:hidden;vertical-align:baseline';
    probe.appendChild(g); probe.appendChild(m);
    doc.body.appendChild(probe);
    var off = m.getBoundingClientRect().top - g.getBoundingClientRect().top;
    doc.body.removeChild(probe);
    return off;
  }
  /* the footer paints its chrome with background-clip:text — reuse those stops */
  function chromeStops() {
    var cs = getComputedStyle(span);
    var m = (cs.backgroundImage || '').match(/rgba?\([^)]+\)/g);
    if (!m || m.length < 2) {
      var c = cs.webkitTextFillColor && cs.webkitTextFillColor !== 'transparent'
        ? cs.webkitTextFillColor : (cs.color || 'rgb(255,255,255)');
      return [c, c];
    }
    return m;
  }

  /* ------------------------------------------------------------ injection */
  function injectStyles() {
    var css = [
      '.unt-layer{position:fixed;pointer-events:none}',
      /* NOTE: the blackout is painted ON the canvas, not by a full-screen <div>.
         A black fixed-position overlay div is exactly the shape ad blockers
         cosmetically hide (anti-adblock-wall filters), and one such extension
         was setting display:none on it — the site never went dark. Painting it
         into the canvas that already owns the effect removes that whole class
         of failure, for us and for any customer running a blocker. */
      '.unt-canvas{inset:0;opacity:0;z-index:' + (CFG.zIndex + 2) + ';transition:opacity 420ms linear}',
      '.unt-canvas.is-on{opacity:1}',
      '.unt-msg{z-index:' + (CFG.zIndex + 4) + ';transform:translate(-50%,-50%);text-align:center;color:#fff}',
      '.unt-msg p{margin:0 0 1.5em;font-weight:' + CFG.messageWeight + ';',
      '  ' + (CFG.messageUppercase ? 'text-transform:uppercase;' : ''),
      '  font-size:clamp(11px,1.18vw,16px);letter-spacing:' + CFG.messageTracking + ';',
      '  text-indent:' + CFG.messageTracking + ';line-height:2.45;opacity:0;filter:blur(6px);',
      '  transform:translateY(7px);text-shadow:0 0 26px rgba(255,255,255,.22);',
      '  transition:opacity 1500ms cubic-bezier(.22,.61,.36,1),filter 1500ms cubic-bezier(.22,.61,.36,1),',
      '             transform 1500ms cubic-bezier(.22,.61,.36,1)}',
      '.unt-msg p:last-child{margin-bottom:0}',
      '.unt-msg p.is-in{opacity:.94;filter:blur(0);transform:translateY(0)}',
      '.unt-msg.is-out p{opacity:0;filter:blur(5px)}',
      '.unt-hint{top:34px;left:50%;transform:translateX(-50%);z-index:' + (CFG.zIndex + 4) + ';',
      '  font-size:9.5px;letter-spacing:.34em;text-transform:uppercase;color:#fff;opacity:0;',
      '  transition:opacity 1600ms linear}',
      '.unt-hint.is-in{opacity:.22}',
      '.unt-hit{position:absolute;z-index:' + CFG.hitZIndex + ';display:block;padding:0;margin:0;border:0;',
      '  background:transparent;cursor:pointer;-webkit-appearance:none;appearance:none;font:inherit;color:transparent}',
      '.unt-hit:focus{outline:none}',
      '.unt-hit:focus-visible{outline:1px solid rgba(255,255,255,.28);outline-offset:6px}',
      '.unt-sr{position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0 0 0 0);white-space:nowrap}'
    ].join('');
    var s = doc.createElement('style');
    s.setAttribute('data-unt', '');
    s.appendChild(doc.createTextNode(css));
    doc.head.appendChild(s);
  }

  function el(tag, cls) { var e = doc.createElement(tag); e.className = cls; return e; }

  function buildLayers() {
    canvas = el('canvas', 'unt-layer unt-canvas');
    msg    = el('div', 'unt-layer unt-msg');
    hint   = el('div', 'unt-layer unt-hint');
    hint.textContent = CFG.hintText;
    msg.setAttribute('aria-hidden', 'true');

    for (var i = 0; i < CFG.lines.length; i++) {
      var p = doc.createElement('p');
      p.textContent = CFG.lines[i];
      msg.appendChild(p);
      lineEls.push(p);
    }
    srLive = el('div', 'unt-sr');
    srLive.setAttribute('role', 'status');
    srLive.setAttribute('aria-live', 'polite');

    hitArea = doc.createElement('button');
    hitArea.type = 'button';
    hitArea.className = 'unt-hit';
    hitArea.setAttribute('aria-label', span.textContent.trim() || 'Until next time');
    hitArea.addEventListener('click', function (e) {
      e.preventDefault(); e.stopPropagation(); open();
    });

    [canvas, msg, hint, srLive, hitArea].forEach(function (n) {
      doc.body.appendChild(n);
    });
    ctx = canvas.getContext('2d');

    // inherit the wordmark's typeface for the message
    var cs = getComputedStyle(span);
    msg.style.fontFamily = cs.fontFamily;
    hint.style.fontFamily = cs.fontFamily;
  }

  function placeHitArea() {
    if (!hitArea) return;
    var r = wordRect();
    if (!r.width) { hitArea.style.display = 'none'; return; }
    hitArea.style.display = 'block';
    hitArea.style.left   = (r.left + window.pageXOffset) + 'px';
    hitArea.style.top    = (r.top  + window.pageYOffset) + 'px';
    hitArea.style.width  = r.width + 'px';
    hitArea.style.height = r.height + 'px';
  }

  /* ------------------------------------------------------------- masking */
  function maskWord(on) {
    if (!on) {
      span.style.webkitMaskImage = ''; span.style.maskImage = '';
      if (!span.getAttribute('style')) span.removeAttribute('style');  // leave no trace
      return;
    }
    var sr = span.getBoundingClientRect(), wr = wordRect();
    var x1 = Math.max(0, wr.left - sr.left - 2);
    var x2 = Math.min(sr.width, wr.right - sr.left + 2);
    var g = 'linear-gradient(to right,#000 0,#000 ' + x1 + 'px,rgba(0,0,0,0) ' + x1 + 'px,' +
            'rgba(0,0,0,0) ' + x2 + 'px,#000 ' + x2 + 'px,#000 100%)';
    span.style.webkitMaskImage = g;
    span.style.maskImage = g;
  }

  /* ------------------------------------------------------------- sprites */
  function makeSprite(r, g, b) {
    var size = 24, c = doc.createElement('canvas');
    c.width = c.height = size;
    var x = c.getContext('2d');
    var grd = x.createRadialGradient(size / 2, size / 2, 0, size / 2, size / 2, size / 2);
    grd.addColorStop(0, 'rgba(' + r + ',' + g + ',' + b + ',1)');
    grd.addColorStop(0.30, 'rgba(' + r + ',' + g + ',' + b + ',0.48)');
    grd.addColorStop(0.62, 'rgba(' + r + ',' + g + ',' + b + ',0.11)');
    grd.addColorStop(1, 'rgba(' + r + ',' + g + ',' + b + ',0)');
    x.fillStyle = grd; x.fillRect(0, 0, size, size);
    return c;
  }
  function buildSprites() {
    sprites = [];
    var i, v;
    for (i = 0; i < 9; i++) { v = Math.round(96 + i * 19.5); sprites.push(makeSprite(v, v, Math.min(255, Math.round(v * 1.01)))); }
    for (i = 0; i < 9; i++) { v = Math.round(120 + i * 16.8); sprites.push(makeSprite(Math.min(255, v), Math.round(v * .985), Math.round(v * .93))); }
  }

  /* ------------------------------------------------------------- sampling */
  function buildParticles() {
    var box = wordRect();
    if (!box.width || !box.height) return false;
    wordBox = box;

    var pad = 12;
    var sdpr = Math.min(window.devicePixelRatio || 1, 2);
    var off = doc.createElement('canvas');
    off.width  = Math.ceil((box.width  + pad * 2) * sdpr);
    off.height = Math.ceil((box.height + pad * 2) * sdpr);
    var o = off.getContext('2d', { willReadFrequently: true });
    o.scale(sdpr, sdpr);

    var cs = getComputedStyle(span);
    o.font = cs.fontStyle + ' ' + cs.fontWeight + ' ' + cs.fontSize + ' ' + cs.fontFamily;
    o.textBaseline = 'alphabetic';
    o.fillStyle = '#fff';

    var base = baselineOffset();
    var chars = charRects();
    var spanRect = span.getBoundingClientRect();
    var stops = chromeStops();

    /* A second, chrome-filled copy of the word. The canvas takes ownership of
       the letters the instant the egg opens, so the site can go black behind
       them while they still look exactly as the footer painted them. */
    wordCanvas = doc.createElement('canvas');
    wordCanvas.width = off.width; wordCanvas.height = off.height;
    var wc = wordCanvas.getContext('2d');
    wc.scale(sdpr, sdpr);
    wc.font = o.font; wc.textBaseline = 'alphabetic';
    var gy0 = spanRect.top - (box.top - pad);
    var wg = wc.createLinearGradient(0, gy0, 0, gy0 + spanRect.height);
    for (var gs = 0; gs < stops.length; gs++) wg.addColorStop(gs / (stops.length - 1), stops[gs]);
    wc.fillStyle = wg;

    for (var i = 0; i < chars.length; i++) {
      var r = chars[i].rect;
      if (!chars[i].ch.trim()) continue;
      var dx = r.left - box.left + pad, dy = r.top - box.top + pad + base;
      o.fillText(chars[i].ch, dx, dy);     // white — alpha mask + shimmer stencil
      wc.fillText(chars[i].ch, dx, dy);    // chrome — what the viewer sees
    }

    glyph = off;
    wordDraw = { x: box.left - pad, y: box.top - pad, w: off.width / sdpr, h: off.height / sdpr };

    var img;
    try { img = o.getImageData(0, 0, off.width, off.height); } catch (e) { return false; }
    var data = img.data, iw = off.width, ih = off.height;

    var lowPower = (navigator.hardwareConcurrency || 8) <= 4;
    var budget = (window.innerWidth < 700 || lowPower) ? 2200 : 3800;

    var pts = [];
    for (var y = 0; y < ih; y++) {
      for (var x = 0; x < iw; x++) {
        if (data[(y * iw + x) * 4 + 3] > 130) pts.push(x + y * iw);
      }
    }
    if (pts.length > budget) {
      for (var q = pts.length - 1; q > 0; q--) {
        var j = (Math.random() * (q + 1)) | 0, t = pts[q]; pts[q] = pts[j]; pts[j] = t;
      }
      pts.length = budget;
    }

    // colour ramp taken from the footer's own gradient
    var ramp = doc.createElement('canvas'); ramp.width = 1; ramp.height = 64;
    var rc = ramp.getContext('2d');
    var rg = rc.createLinearGradient(0, 0, 0, 64);
    for (var s = 0; s < stops.length; s++) rg.addColorStop(s / (stops.length - 1), stops[s]);
    rc.fillStyle = rg; rc.fillRect(0, 0, 1, 64);
    var rampData = rc.getImageData(0, 0, 1, 64).data;

    particles.length = 0;
    for (var k = 0; k < pts.length; k++) {
      var px = pts[k] % iw, py = (pts[k] / iw) | 0;
      var vx = box.left - pad + px / sdpr;
      var vy = box.top  - pad + py / sdpr;
      var g01 = Math.max(0, Math.min(0.999, (vy - spanRect.top) / Math.max(1, spanRect.height)));
      var ri = ((g01 * 64) | 0) * 4;
      var lum = rampData[ri] * .299 + rampData[ri + 1] * .587 + rampData[ri + 2] * .114;
      particles.push({
        hx: vx, hy: vy, x: vx, y: vy, vx: 0, vy: 0,
        r: 0, th: 0, vr: 0, r0: 0, th0: 0, dTh: 0,
        lvl: Math.max(0, Math.min(8, Math.round((lum - 92) / 19.5))),
        size: 1.5 + Math.random() * 2.3,
        a: .55 + Math.random() * .45,
        k: .055 + Math.random() * .045,
        band: Math.random(),
        inner: Math.random() < .16,
        phase: Math.random() * Math.PI * 2,
        spin: .85 + Math.random() * .5,
        delay: 0
      });
    }
    var minx = Infinity, maxx = -Infinity, i2;
    for (i2 = 0; i2 < particles.length; i2++) {
      if (particles[i2].hx < minx) minx = particles[i2].hx;
      if (particles[i2].hx > maxx) maxx = particles[i2].hx;
    }
    var sp = Math.max(1, maxx - minx);
    for (i2 = 0; i2 < particles.length; i2++) particles[i2].delay = ((particles[i2].hx - minx) / sp) * 220;

    sweep = doc.createElement('canvas');
    sweep.width = off.width; sweep.height = off.height;
    sweep._dpr = sdpr; sweep._pad = pad;

    return particles.length > 40;
  }

  /* --------------------------------------------------------------- canvas */
  function sizeCanvas() {
    if (!canvas) return;
    DPR = Math.min(window.devicePixelRatio || 1, 2);
    W = window.innerWidth; H = window.innerHeight;
    canvas.width = Math.floor(W * DPR); canvas.height = Math.floor(H * DPR);
    canvas.style.width = W + 'px'; canvas.style.height = H + 'px';
    ctx.setTransform(DPR, 0, 0, DPR, 0, 0);
    cx = W / 2; cy = H * CFG.portalYRatio;
    R = Math.max(Math.min(W * .40, 168), Math.min(320, Math.min(W, H) * .275));
  }
  function placeOverlays() {
    if (!msg) return;
    msg.style.left = cx + 'px'; msg.style.top = cy + 'px';
    msg.style.width = Math.min(W - 64, Math.round(R * 2.05)) + 'px';
  }

  function seedDust() {
    dust.length = 0;
    var n = (W < 700) ? 60 : 110;
    for (var i = 0; i < n; i++) dust.push(newDust(true));
  }
  function newDust(spread) {
    var ang = Math.random() * Math.PI * 2;
    var rad = spread ? Math.random() * R * 1.5 : R * (.55 + Math.random() * .5);
    return {
      x: cx + Math.cos(ang) * rad, y: cy + Math.sin(ang) * rad,
      vx: (Math.random() - .5) * .16, vy: -.05 - Math.random() * .2,
      life: 0, max: 2600 + Math.random() * 5200,
      size: .9 + Math.random() * 2.1, a: .14 + Math.random() * .3
    };
  }

  /* ---------------------------------------------------------------- audio */
  function playWhoosh() {
    if (!CFG.sound) return;
    try {
      var AC = window.AudioContext || window.webkitAudioContext; if (!AC) return;
      var ac = new AC(), dur = 2.6, sr = ac.sampleRate;
      var buf = ac.createBuffer(1, Math.ceil(sr * dur), sr), d = buf.getChannelData(0);
      var b0 = 0, b1 = 0, b2 = 0;
      for (var i = 0; i < d.length; i++) {
        var w = Math.random() * 2 - 1;
        b0 = .99765 * b0 + w * .0990460;
        b1 = .96300 * b1 + w * .2965164;
        b2 = .57000 * b2 + w * 1.0526913;
        d[i] = (b0 + b1 + b2 + w * .1848) * .14;
      }
      var t0 = ac.currentTime;
      var src = ac.createBufferSource(); src.buffer = buf;
      var hp = ac.createBiquadFilter(); hp.type = 'highpass'; hp.frequency.value = 130;
      var bp = ac.createBiquadFilter(); bp.type = 'bandpass'; bp.Q.value = 1.05;
      bp.frequency.setValueAtTime(200, t0);
      bp.frequency.exponentialRampToValueAtTime(1700, t0 + .95);
      bp.frequency.exponentialRampToValueAtTime(240, t0 + 2.45);
      var g = ac.createGain();
      g.gain.setValueAtTime(.0001, t0);
      g.gain.exponentialRampToValueAtTime(.05, t0 + .6);
      g.gain.exponentialRampToValueAtTime(.0001, t0 + 2.5);
      var osc = ac.createOscillator(); osc.type = 'sine';
      osc.frequency.setValueAtTime(36, t0);
      osc.frequency.exponentialRampToValueAtTime(58, t0 + 1.7);
      var og = ac.createGain();
      og.gain.setValueAtTime(.0001, t0);
      og.gain.exponentialRampToValueAtTime(.026, t0 + .9);
      og.gain.exponentialRampToValueAtTime(.0001, t0 + 2.7);
      src.connect(hp); hp.connect(bp); bp.connect(g); g.connect(ac.destination);
      osc.connect(og); og.connect(ac.destination);
      src.start(t0); osc.start(t0); osc.stop(t0 + 2.8);
      setTimeout(function () { try { ac.close(); } catch (e) {} }, 3200);
    } catch (e) {}
  }

  /* ------------------------------------------------------- scroll locking */
  function noScroll(e) { e.preventDefault(); }
  function noScrollKeys(e) {
    if ([' ', 'PageUp', 'PageDown', 'End', 'Home', 'ArrowUp', 'ArrowDown'].indexOf(e.key) > -1) e.preventDefault();
  }
  function snapBack() { window.scrollTo(window.pageXOffset, lockY); }
  function lockScroll() {
    lockY = window.pageYOffset;                 // no overflow:hidden — that would
    window.addEventListener('wheel', noScroll, { passive: false });   // reflow the
    window.addEventListener('touchmove', noScroll, { passive: false });// page when
    window.addEventListener('keydown', noScrollKeys, false);           // the bar goes
    window.addEventListener('scroll', snapBack, false);
  }
  function unlockScroll() {
    window.removeEventListener('wheel', noScroll, { passive: false });
    window.removeEventListener('touchmove', noScroll, { passive: false });
    window.removeEventListener('keydown', noScrollKeys, false);
    window.removeEventListener('scroll', snapBack, false);
  }

  /* ----------------------------------------------------------- open/close */
  function open() {
    if (state !== 'idle') return;
    openedAt = 0; closing = false;
    sizeCanvas(); placeOverlays();
    if (!buildParticles()) return;

    lockScroll();
    srLive.textContent = CFG.lines.join(' ');
    doc.addEventListener('keydown', onKey, true);
    window.addEventListener('click', onDismiss, true);
    playWhoosh();

    if (mqReduce.matches) { openReduced(); return; }

    seedDust();
    masked = false;
    state = 'shimmer'; shimmerT = 0;
    canvas.style.transition = 'none';        // the canvas word must appear instantly
    canvas.style.pointerEvents = 'auto';     // and swallow clicks meant for the page
    canvas.classList.add('is-on');
    startLoop();                             // the blackout is painted in render()
  }

  function openReduced() {
    reduced = true;
    state = 'portal';
    masked = false;
    canvas.style.transition = 'none';
    canvas.style.pointerEvents = 'auto';
    canvas.classList.add('is-on');
    startLoop();                  // eases the blackout in, draws a still ring
    var delays = [900, 1650, 2450];
    lineEls.forEach(function (p, i) {
      setTimeout(function () { if (!closing) p.classList.add('is-in'); }, delays[i] || 2450);
    });
    setTimeout(function () { if (!closing) hint.classList.add('is-in'); }, 4200);
  }

  function startFracture() {
    if (!masked) maskWord(true);    // safety: the word must have left the footer
    var lx = wordBox.left + wordBox.width / 2, ly = wordBox.top + wordBox.height / 2;
    for (var i = 0; i < particles.length; i++) {
      var p = particles[i];
      var dx = p.x - lx, dy = p.y - ly;
      var dist = Math.max(6, Math.sqrt(dx * dx + dy * dy));
      var push = .7 + Math.random() * 1.5;
      p.vx = (dx / dist) * push + (Math.random() - .5) * 1.2;
      p.vy = (dy / dist) * push * .55 - .35 - Math.random() * 1.2;
    }
    state = 'fracture'; phaseT = 0;
  }

  function close() {
    if (state === 'idle' || closing) return;
    closing = true;
    msgOut = true;
    msg.classList.add('is-out');
    lineEls.forEach(function (p) { p.classList.remove('is-in'); });
    hint.classList.remove('is-in');
    canvas.style.transition = '';          // let it fade out gently
    canvas.style.pointerEvents = '';
    doc.removeEventListener('keydown', onKey, true);
    window.removeEventListener('click', onDismiss, true);

    if (reduced) { state = 'return'; returnT = 0; startLoop(); return; }
    if (state === 'shimmer') { finish(); return; }
    if (state === 'portal') toCartesian();
    state = 'return'; returnT = 0;
    startLoop();
  }
  function forceClose() { close(); setTimeout(finish, 40); }

  function finish() {
    if (raf) { cancelAnimationFrame(raf); raf = null; }
    lastTs = 0; acc = 0;
    canvas.classList.remove('is-on');
    if (ctx) ctx.clearRect(0, 0, W, H);
    maskWord(false);
    msg.classList.remove('is-out');
    unlockScroll();
    srLive.textContent = '';
    canvas.style.transition = ''; canvas.style.pointerEvents = '';
    state = 'idle'; closing = false; portalAlpha = 0; veilA = 0;
    msgOut = false; masked = false; reduced = false;
    placeHitArea();
    try { hitArea.focus({ preventScroll: true }); } catch (e) {}
  }

  /* --------------------------------------------------------------- events */
  function onKey(e) { if (e.key === 'Escape' || e.key === 'Esc') { e.preventDefault(); close(); } }
  function onDismiss(e) {
    if (state === 'idle' || closing) return;
    if (openedAt < 420) return;
    e.preventDefault(); e.stopPropagation(); close();
  }

  /* ---------------------------------------------------------------- maths */
  function shortestAngle(from, to) {
    var d = (to - from) % (Math.PI * 2);
    if (d > Math.PI) d -= Math.PI * 2;
    if (d < -Math.PI) d += Math.PI * 2;
    return d;
  }
  function easeOutCubic(t) { return 1 - Math.pow(1 - t, 3); }
  function easeInOutCubic(t) { return t < .5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2; }
  function ringRadius(p, breathe, t) {
    if (p.inner) return R * (.17 + p.band * .56) * breathe;
    return R * (.87 + p.band * p.band * .24) * breathe + Math.sin(t * .0012 + p.phase) * R * .035;
  }
  function toPolar() {
    var GOLD = 2.399963229728653;
    for (var i = 0; i < particles.length; i++) {
      var p = particles[i], dx = p.x - cx, dy = p.y - cy;
      p.r0 = Math.max(6, Math.sqrt(dx * dx + dy * dy));
      p.th0 = Math.atan2(dy, dx);
      p.r = p.r0; p.th = p.th0; p.vr = 0;
      p.dTh = shortestAngle(p.th0, (i * GOLD) % (Math.PI * 2)) + (Math.PI * 2) * (.85 + p.band * .85);
    }
    gatherG = 0; state = 'portal'; portalT = 0; msgOut = false;
  }
  function toCartesian() {
    for (var i = 0; i < particles.length; i++) {
      var p = particles[i];
      p.x = cx + Math.cos(p.th) * p.r;
      p.y = cy + Math.sin(p.th) * p.r;
      p.vx = -Math.sin(p.th) * .9;
      p.vy = Math.cos(p.th) * .9;
    }
  }

  /* ----------------------------------------------------------------- loop */
  function startLoop() { if (!raf) { lastTs = 0; acc = 0; raf = requestAnimationFrame(frame); } }
  function frame(ts) {
    if (!lastTs) lastTs = ts;
    var dt = Math.min(ts - lastTs, 68); lastTs = ts; acc += dt;
    var steps = 0;
    while (acc >= STEP && steps < 3) { update(STEP); acc -= STEP; steps++; }
    if (acc > STEP) acc = 0;
    render();
    if (state === 'idle') { raf = null; return; }
    raf = requestAnimationFrame(frame);
  }

  function update(ms) {
    openedAt += ms;
    var i, p;

    // the blackout, eased on the canvas rather than via an overlay element
    if (state === 'return' || closing) veilA = Math.max(0, veilA - ms / 850);
    else veilA = Math.min(1, veilA + ms / CFG.blackout);

    if (reduced) {
      if (state === 'return' && veilA <= 0) finish();
      return;
    }

    if (state === 'shimmer') {
      shimmerT += ms;
      if (shimmerT >= CFG.shimmer) startFracture();
      return;
    }
    if (state === 'fracture') {
      phaseT += ms;
      for (i = 0; i < particles.length; i++) {
        p = particles[i];
        p.x += p.vx; p.y += p.vy; p.vx *= .972; p.vy *= .972;
      }
      portalAlpha = Math.min(1, phaseT / 900);
      if (phaseT >= CFG.fracture) toPolar();
      return;
    }
    if (state === 'portal') {
      portalT += ms;
      portalAlpha = Math.min(1, portalAlpha + ms / 900);
      var breathe = 1 + .045 * Math.sin(portalT * .00085);
      if (gatherG < 1) {
        gatherG = Math.min(1, gatherG + ms / CFG.gather);
        var eth = easeOutCubic(gatherG), er = easeInOutCubic(gatherG);
        for (i = 0; i < particles.length; i++) {
          p = particles[i];
          p.th = p.th0 + p.dTh * eth;
          p.r = p.r0 + (ringRadius(p, breathe, portalT) - p.r0) * er;
          p.vr = 0;
        }
      } else {
        for (i = 0; i < particles.length; i++) {
          p = particles[i];
          p.vr += (ringRadius(p, breathe, portalT) - p.r) * (.030 + p.k * .16);
          p.vr *= .885; p.r += p.vr;
          p.th += .0055 * p.spin * (1 + (R / Math.max(40, p.r) - 1) * .55);
        }
      }
      updateDust(ms); timeline();
      return;
    }
    if (state === 'return') {
      returnT += ms;
      portalAlpha = Math.max(0, portalAlpha - ms / 700);
      var maxD = 0, maxV = 0;
      for (i = 0; i < particles.length; i++) {
        p = particles[i];
        if (returnT < p.delay) continue;
        var k = .052 + p.k * .5;
        p.vx += (p.hx - p.x) * k; p.vy += (p.hy - p.y) * k;
        p.vx *= .815; p.vy *= .815;
        p.x += p.vx; p.y += p.vy;
        var ddx = p.hx - p.x, ddy = p.hy - p.y, d2 = ddx * ddx + ddy * ddy;
        if (d2 > maxD) maxD = d2;
        var v2 = p.vx * p.vx + p.vy * p.vy; if (v2 > maxV) maxV = v2;
      }
      updateDust(ms, true);
      if ((returnT > 700 && maxD < .36 && maxV < .36) || returnT > 3000) {
        for (i = 0; i < particles.length; i++) { particles[i].x = particles[i].hx; particles[i].y = particles[i].hy; }
        finish();
      }
    }
  }

  function updateDust(ms, dying) {
    for (var i = 0; i < dust.length; i++) {
      var d = dust[i];
      d.life += ms; d.x += d.vx; d.y += d.vy;
      d.vx += (Math.random() - .5) * .012;
      if (d.life > d.max) { if (dying) d.a = 0; else dust[i] = newDust(false); }
    }
  }

  function timeline() {
    if (closing) return;
    var t = portalT, i;
    for (i = 0; i < lineEls.length; i++) {
      if (t >= CFG.msgIn + i * CFG.lineGap && !msgOut && !lineEls[i].classList.contains('is-in')) {
        lineEls[i].classList.add('is-in');
      }
    }
    var allIn = CFG.msgIn + (lineEls.length - 1) * CFG.lineGap + 1500;
    if (t >= allIn + CFG.msgHold && !msgOut) {
      msgOut = true;
      msg.classList.add('is-out');
      for (i = 0; i < lineEls.length; i++) lineEls[i].classList.remove('is-in');
    }
    if (msgOut) {
      var since = t - (allIn + CFG.msgHold);
      if (since >= 1500 + CFG.quietHint && !hint.classList.contains('is-in')) hint.classList.add('is-in');
      if (since >= 1500 + CFG.autoReturn) close();
    }
  }

  /* --------------------------------------------------------------- render */
  function render() {
    if (!ctx) return;
    ctx.clearRect(0, 0, W, H);
    if (state === 'idle') return;

    // 1. the site going dark — painted, not overlaid
    if (veilA > 0) {
      ctx.globalCompositeOperation = 'source-over';
      ctx.fillStyle = 'rgba(0,0,0,' + veilA.toFixed(3) + ')';
      ctx.fillRect(0, 0, W, H);
    }

    // reduced motion: a still ring, no particles, no drift
    if (reduced) {
      ctx.globalCompositeOperation = 'lighter';
      var rg = ctx.createRadialGradient(cx, cy, R * .62, cx, cy, R * 1.35);
      rg.addColorStop(0, 'rgba(255,255,255,0)');
      rg.addColorStop(.6, 'rgba(226,232,240,' + (.05 * veilA) + ')');
      rg.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = rg;
      ctx.beginPath(); ctx.arc(cx, cy, R * 1.35, 0, Math.PI * 2); ctx.fill();
      ctx.strokeStyle = 'rgba(255,255,255,' + (.16 * veilA) + ')';
      ctx.lineWidth = 1;
      ctx.beginPath(); ctx.arc(cx, cy, R, 0, Math.PI * 2); ctx.stroke();
      ctx.globalCompositeOperation = 'source-over';
      return;
    }

    /* the canvas now owns the letters: draw them exactly where the footer had
       them, then sweep a light across. The DOM word is masked out on this same
       frame, so the handoff is invisible and the veil can darken behind it. */
    if (state === 'shimmer') {
      ctx.globalCompositeOperation = 'source-over';
      ctx.drawImage(wordCanvas, wordDraw.x, wordDraw.y, wordDraw.w, wordDraw.h);
      if (!masked) { maskWord(true); masked = true; }

      var g01 = shimmerT / CFG.shimmer;
      var sc = sweep.getContext('2d');
      sc.clearRect(0, 0, sweep.width, sweep.height);
      sc.drawImage(glyph, 0, 0);
      sc.globalCompositeOperation = 'source-in';
      var band = (-0.35 + g01 * 1.7) * sweep.width;
      var gr = sc.createLinearGradient(band - sweep.width * .34, 0, band + sweep.width * .34, sweep.height);
      gr.addColorStop(0, 'rgba(255,255,255,0)');
      gr.addColorStop(.5, 'rgba(255,255,255,' + (0.85 * Math.sin(Math.min(1, g01) * Math.PI)) + ')');
      gr.addColorStop(1, 'rgba(255,255,255,0)');
      sc.fillStyle = gr; sc.fillRect(0, 0, sweep.width, sweep.height);
      sc.globalCompositeOperation = 'source-over';
      ctx.globalCompositeOperation = 'lighter';
      ctx.drawImage(sweep,
        wordBox.left - sweep._pad, wordBox.top - sweep._pad,
        sweep.width / sweep._dpr, sweep.height / sweep._dpr);
      ctx.globalCompositeOperation = 'source-over';
      return;
    }

    var pa = portalAlpha;
    if (pa > .01 && state !== 'return') {
      ctx.globalCompositeOperation = 'source-over';
      var core = ctx.createRadialGradient(cx, cy, 0, cx, cy, R * 1.02);
      core.addColorStop(0, 'rgba(0,0,0,' + (.52 * pa) + ')');
      core.addColorStop(.62, 'rgba(0,0,0,' + (.34 * pa) + ')');
      core.addColorStop(1, 'rgba(0,0,0,0)');
      ctx.fillStyle = core;
      ctx.beginPath(); ctx.arc(cx, cy, R * 1.02, 0, Math.PI * 2); ctx.fill();
    }

    ctx.globalCompositeOperation = 'lighter';
    if (pa > .01) {
      var halo = ctx.createRadialGradient(cx, cy, R * .62, cx, cy, R * 1.5);
      halo.addColorStop(0, 'rgba(255,255,255,0)');
      halo.addColorStop(.52, 'rgba(222,229,238,' + (.030 * pa) + ')');
      halo.addColorStop(.72, 'rgba(255,255,255,' + (.018 * pa) + ')');
      halo.addColorStop(1, 'rgba(255,255,255,0)');
      ctx.fillStyle = halo;
      ctx.beginPath(); ctx.arc(cx, cy, R * 1.5, 0, Math.PI * 2); ctx.fill();
    }

    var warm = (state === 'portal') ? 9 : 0, i, p, s;
    if (state === 'portal') {
      for (i = 0; i < particles.length; i++) {
        p = particles[i];
        var px = cx + Math.cos(p.th) * p.r, py = cy + Math.sin(p.th) * p.r;
        s = p.size * (p.inner ? 1.5 : 2.5);
        ctx.globalAlpha = p.a * (p.inner ? .5 : .86);
        ctx.drawImage(sprites[p.lvl + warm], px - s / 2, py - s / 2, s, s);
      }
    } else {
      for (i = 0; i < particles.length; i++) {
        p = particles[i]; s = p.size * 2.35;
        ctx.globalAlpha = p.a * .9;
        ctx.drawImage(sprites[p.lvl], p.x - s / 2, p.y - s / 2, s, s);
      }
    }

    if (pa > .01) {
      var bright = sprites[16];
      for (i = 0; i < dust.length; i++) {
        var d = dust[i];
        var lifeF = 1 - d.life / d.max;
        var fade = Math.min(1, lifeF * 3) * Math.min(1, d.life / 700);
        ctx.globalAlpha = d.a * Math.max(0, fade) * pa;
        var ds = d.size * 3.2;
        ctx.drawImage(bright, d.x - ds / 2, d.y - ds / 2, ds, ds);
      }
    }
    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
  }

  /* ------------------------------------------------------------------ go */
  if (doc.readyState === 'loading') doc.addEventListener('DOMContentLoaded', init);
  else init();

  window.__unt = { open: open, close: close, get state() { return state; }, get count() { return particles.length; } };
})();
